<template>
  <div
    class="register-page pt-4 px-3 pb-2 city-signup-page"
    :class="{ 'city-signup-page--has-intro': !!eventIntroText }"
  >
    <div class="container" style="max-width: 980px;">
      <div class="city-signup-banner-wrap mb-3">
        <template v-if="!loading && !errorText">
          <img
            :key="bannerRenderKey"
            :src="bannerUrl"
            alt="city-signup-banner"
            class="city-signup-banner-img"
            loading="lazy"
          />
        </template>
        <div v-else class="city-signup-banner-skeleton" aria-hidden="true"></div>
      </div>
      <div class="card-box city-signup-card">
        <div class="city-signup-head mb-3">
          <h3 class="mb-0 city-signup-title">{{ detail.examName || '赛事信息' }}</h3>
        </div>

        <div v-if="loading" class="text-muted">正在加载报名场次信息...</div>
        <div v-else-if="errorText" class="alert alert-danger mb-0">{{ errorText }}</div>
        <div v-else>
          <div
            class="alert mt-2 mb-0"
            :class="signupWindowActive ? 'alert-primary' : 'alert-secondary'"
          >
            当前状态：<strong>{{ signupStatusHeadline }}</strong>
          </div>
          <div class="city-signup-info-grid">
            <div class="city-signup-info-card">
              <div class="k">省份/地市</div>
              <div class="v">{{ detail.province || '-' }} {{ detail.city || '' }}</div>
            </div>
            <div
              v-if="(detail.attachments && detail.attachments.length) || detail.attachmentUrl"
              class="city-signup-info-card"
            >
              <div class="k">赛事附件</div>
              <div class="v">
                <div v-if="detail.attachments && detail.attachments.length" class="city-signup-attach-list">
                  <a
                    v-for="(a, idx) in detail.attachments"
                    :key="'att-' + idx"
                    :href="a.url"
                    class="city-signup-attach-link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {{ a.name || '下载附件' }}
                  </a>
                </div>
                <a
                  v-else
                  :href="detail.attachmentUrl"
                  class="city-signup-attach-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {{ detail.attachmentName || '下载附件' }}
                </a>
              </div>
            </div>
            <div class="city-signup-info-card">
              <div class="k">主办单位</div>
              <div class="v">{{ detail.organizer || '-' }}</div>
            </div>
            <div class="city-signup-info-card">
              <div class="k">考试地点</div>
              <div class="v">{{ detail.venue || '-' }}</div>
            </div>
            <div class="city-signup-info-card">
              <div class="k">报名时间</div>
              <div class="v">{{ $fmtDateTime(detail.signupStartTime) }} ~ {{ $fmtDateTime(detail.signupEndTime) }}</div>
            </div>
            <div class="city-signup-info-card">
              <div class="k">考试时间</div>
              <div class="v">{{ $fmtDateTime(detail.examTime) }}</div>
            </div>
            <div v-if="eventIntroText" class="city-signup-info-card city-signup-intro-card">
              <div class="k">赛事介绍</div>
              <div class="v city-signup-intro-text">{{ eventIntroText }}</div>
            </div>
          </div>

          <div v-if="Number(detail.autoGenerateExamAccount) === 1" class="alert alert-warning mt-3 mb-0">
            本场次已开启「自动生成考试账号」：报名成功后，请及时下载保存账号密码文件。
          </div>
          <div v-if="submitMsg" class="alert alert-success mt-3 mb-0">
            {{ submitMsg }}
          </div>
          <div v-if="lastExamAccount" class="border rounded p-3 mt-3 bg-white">
            <div class="fw-bold text-primary mb-2">考试账号信息</div>
            <div class="small mb-1">登录账号：<strong>{{ lastExamAccount.username }}</strong></div>
            <div class="small text-muted mb-2">密码已写入下方下载文件，请妥善保管，勿向他人泄露。</div>
            <button class="btn btn-sm btn-outline-primary" type="button" @click="downloadExamAccountExcel">
              下载账号信息（Excel）
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 为底部固定「赛事报名」栏预留文档流高度，避免正文（尤其赛事介绍）被遮挡 -->
    <div
      v-if="!loading && !errorText"
      class="city-signup-fixed-bar-spacer"
      aria-hidden="true"
    />

    <div class="city-signup-bottom-bar" v-if="!loading && !errorText">
      <div class="city-signup-bottom-inner">
        <button
          class="city-signup-apply-btn-smoke"
          :class="{ 'city-signup-apply-btn-smoke--disabled': isSignupClosed }"
          type="button"
          @click="openApplyModal"
          :disabled="submitting || isSignupClosed"
        >
          <span class="label">{{ signupButtonLabel }}</span>
          <span class="smoke s1" aria-hidden="true"></span>
          <span class="smoke s2" aria-hidden="true"></span>
          <span class="smoke s3" aria-hidden="true"></span>
          <span class="smoke s4" aria-hidden="true"></span>
          <span class="smoke s5" aria-hidden="true"></span>
        </button>
      </div>
    </div>

    <div v-if="showApplyForm" class="city-signup-modal" role="dialog" aria-modal="true">
      <div class="city-signup-modal-backdrop" @click="closeApplyModal"></div>
      <div class="city-signup-modal-card" @click.stop>
        <div class="city-signup-modal-head">
          <div>
            <div class="city-signup-modal-title">填写报名信息</div>
            <div class="city-signup-modal-subtitle text-muted">{{ detail.examName || '赛事报名' }}</div>
          </div>
          <button class="city-signup-modal-close" type="button" @click="closeApplyModal" aria-label="关闭">×</button>
        </div>

        <div class="row g-2">
          <div class="col-md-4">
            <label class="city-signup-field-label">
              姓名 <span v-if="!form.candidateName" class="req-star">*</span>
              <span class="city-signup-field-hint">（请填写真实姓名）</span>
            </label>
            <input v-model.trim="form.candidateName" class="form-control" placeholder="例如：张三" />
          </div>

          <div class="col-md-2">
            <label class="city-signup-field-label">
              性别 <span v-if="!form.gender" class="req-star">*</span>
              <span class="city-signup-field-hint">（请选择）</span>
            </label>
            <select v-model="form.gender" class="form-select">
              <option value="">请选择</option>
              <option value="男">男</option>
              <option value="女">女</option>
            </select>
          </div>

          <div class="col-md-6">
            <label class="city-signup-field-label">
              身份证号 <span v-if="!form.idCard" class="req-star">*</span>
              <span class="city-signup-field-hint">（用于身份核验）</span>
            </label>
            <input v-model.trim="form.idCard" class="form-control" placeholder="例如：110101199001011234" />
          </div>

          <div class="col-md-4">
            <label class="city-signup-field-label">
              联系电话 <span v-if="!form.phone" class="req-star">*</span>
              <span class="city-signup-field-hint">（用于接收通知）</span>
            </label>
            <input v-model.trim="form.phone" class="form-control" placeholder="例如：13800000000" />
          </div>

          <div class="col-md-4">
            <label class="city-signup-field-label">
              学校 <span v-if="!form.schoolName" class="req-star">*</span>
              <span class="city-signup-field-hint">（请填写学校全称）</span>
            </label>
            <input v-model.trim="form.schoolName" class="form-control" placeholder="例如：XX中学/XX大学" />
          </div>

          <div class="col-md-4">
            <label class="city-signup-field-label">
              年级 <span v-if="!form.gradeName" class="req-star">*</span>
              <span class="city-signup-field-hint">（请选择组别/年级）</span>
            </label>
            <select v-model="form.gradeName" class="form-select">
              <option value="">请选择</option>
              <option value="幼儿组">幼儿组</option>
              <option value="小学低年级组（小学一到二年级）">小学低年级组（小学一到二年级）</option>
              <option value="小学高年级组（小学三到六年级）">小学高年级组（小学三到六年级）</option>
              <option value="初中组">初中组</option>
              <option value="高中（职）组">高中（职）组</option>
              <option value="大学组">大学组</option>
            </select>
          </div>

          <div class="col-md-4">
            <label class="city-signup-field-label">
              省份 <span v-if="!form.province" class="req-star">*</span>
              <span class="city-signup-field-hint">（请选择）</span>
            </label>
            <select v-model="form.province" class="form-select">
              <option value="">请选择</option>
              <option v-for="p in provinceOptions" :key="'p-' + p" :value="p">{{ p }}</option>
            </select>
          </div>

          <div class="col-md-4">
            <label class="city-signup-field-label">
              地市 <span v-if="!form.city" class="req-star">*</span>
              <span class="city-signup-field-hint">（请选择）</span>
            </label>
            <select v-model="form.city" class="form-select" :disabled="!form.province">
              <option value="">{{ form.province ? '请选择' : '请先选择省份' }}</option>
              <option v-for="c in cityOptions" :key="'c-' + c" :value="c">{{ c }}</option>
            </select>
          </div>

          <div class="col-md-4">
            <label class="city-signup-field-label">
              参加项目 <span v-if="!form.projectName" class="req-star">*</span>
              <span class="city-signup-field-hint">（请选择项目分类）</span>
            </label>
            <select v-model="form.projectName" class="form-select" :disabled="projectOptions.length === 0">
              <option value="">{{ projectOptions.length ? '请选择' : '暂无可选项目，请联系管理员' }}</option>
              <option v-for="p in projectOptions" :key="'proj-' + (p.id || p.projectName)" :value="p.projectName">
                {{ p.projectName }}
              </option>
            </select>
          </div>
        </div>

        <div class="city-signup-confirm-box mt-3">
          <label class="city-signup-check">
            <input v-model="agreeNotice" type="checkbox" />
            <span>
              我已同意“
              <a class="city-signup-link" href="#" @click.prevent.stop="openPreExamInfo">考前信息</a>
              ”
            </span>
          </label>
          <label class="city-signup-check">
            <input v-model="confirmAccurate" type="checkbox" />
            <span>我已确认上述的信息无误</span>
          </label>
        </div>

        <div class="d-flex gap-2 mt-3">
          <button class="btn btn-primary" :disabled="submitDisabled" @click="openConfirmSubmit">
            提交报名
          </button>
          <button class="btn btn-light" :disabled="submitting" @click="resetForm">重置</button>
        </div>
      </div>
    </div>

    <div v-if="showPreExamInfo" class="city-signup-modal" role="dialog" aria-modal="true">
      <div class="city-signup-modal-backdrop" @click="closePreExamInfo"></div>
      <div class="city-signup-modal-card" @click.stop>
        <div class="city-signup-modal-head">
          <div>
            <div class="city-signup-modal-title">考前信息</div>
            <div class="city-signup-modal-subtitle text-muted">请仔细阅读后再勾选同意</div>
          </div>
          <button class="city-signup-modal-close" type="button" @click="closePreExamInfo" aria-label="关闭">×</button>
        </div>
        <pre class="city-signup-preinfo">{{ preExamInfoText }}</pre>
      </div>
    </div>

    <div v-if="showSignupSuccessModal" class="city-signup-modal city-signup-success-layer" role="dialog" aria-modal="true">
      <div class="city-signup-modal-backdrop" @click="closeSignupSuccessModal"></div>
      <div class="city-signup-modal-card city-signup-success-card" @click.stop>
        <div class="city-signup-modal-head">
          <div>
            <div class="city-signup-modal-title text-success">报名成功</div>
            <div class="city-signup-modal-subtitle text-muted">请仔细阅读以下提示</div>
          </div>
          <button class="city-signup-modal-close" type="button" @click="closeSignupSuccessModal" aria-label="关闭">×</button>
        </div>
        <div class="city-signup-success-body">
          <p v-if="signupSuccessExamTimeText" class="mb-2">
            <strong>考试时间：</strong>{{ signupSuccessExamTimeText }}<br />
            <span class="text-danger fw-bold">请准时参加考试</span>，建议提前了解考点位置与入场要求。
          </p>
          <p v-else class="mb-2 text-muted">
            <span class="text-danger fw-bold">请准时参加考试</span>，并留意考务通知中的具体时间与地点安排。
          </p>
          <template v-if="lastExamAccount">
            <p class="mb-2">
              已为您生成考试登录账号：<strong class="text-primary">{{ lastExamAccount.username }}</strong><br />
              登录密码请通过下方文件查看；<strong>请尽快下载并妥善保管</strong>，勿向他人泄露。
            </p>
            <div class="d-flex flex-wrap gap-2 mt-3">
              <button class="btn btn-primary" type="button" @click="downloadExamAccountExcel">
                下载账号与密码（Excel）
              </button>
              <button class="btn btn-outline-secondary" type="button" @click="closeSignupSuccessModal">我知道了</button>
            </div>
          </template>
          <template v-else>
            <p class="mb-3">{{ submitMsg || '您的报名信息已提交。' }}</p>
            <button class="btn btn-primary" type="button" @click="closeSignupSuccessModal">我知道了</button>
          </template>
        </div>
      </div>
    </div>

    <div v-if="showConfirmSubmit" class="city-signup-modal" role="dialog" aria-modal="true">
      <div class="city-signup-modal-backdrop" @click="closeConfirmSubmit"></div>
      <div class="city-signup-modal-card city-signup-modal-card--confirm" @click.stop>
        <div class="city-signup-modal-head">
          <div>
            <div class="city-signup-modal-title">信息核验</div>
            <div class="city-signup-modal-subtitle text-muted">
              请逐项核对信息，{{ confirmCooldown }} 秒后可确认提交
            </div>
          </div>
          <button class="city-signup-modal-close" type="button" @click="closeConfirmSubmit" aria-label="关闭">×</button>
        </div>

        <div class="city-signup-preview">
          <div class="city-signup-preview-title">本次填写信息</div>
          <div class="city-signup-preview-grid">
            <div class="item"><div class="k">姓名</div><div class="v">{{ previewSnapshot && previewSnapshot.candidateName ? previewSnapshot.candidateName : '-' }}</div></div>
            <div class="item"><div class="k">性别</div><div class="v">{{ previewSnapshot && previewSnapshot.gender ? previewSnapshot.gender : '-' }}</div></div>
            <div class="item"><div class="k">身份证号</div><div class="v">{{ previewSnapshot && previewSnapshot.idCard ? previewSnapshot.idCard : '-' }}</div></div>
            <div class="item"><div class="k">联系电话</div><div class="v">{{ previewSnapshot && previewSnapshot.phone ? previewSnapshot.phone : '-' }}</div></div>
            <div class="item"><div class="k">省份</div><div class="v">{{ previewSnapshot && previewSnapshot.province ? previewSnapshot.province : '-' }}</div></div>
            <div class="item"><div class="k">地市</div><div class="v">{{ previewSnapshot && previewSnapshot.city ? previewSnapshot.city : '-' }}</div></div>
            <div class="item"><div class="k">学校</div><div class="v">{{ previewSnapshot && previewSnapshot.schoolName ? previewSnapshot.schoolName : '-' }}</div></div>
            <div class="item"><div class="k">年级</div><div class="v">{{ previewSnapshot && previewSnapshot.gradeName ? previewSnapshot.gradeName : '-' }}</div></div>
            <div class="item wide"><div class="k">参加项目</div><div class="v">{{ previewSnapshot && previewSnapshot.projectName ? previewSnapshot.projectName : '-' }}</div></div>
          </div>
        </div>

        <div class="d-flex gap-2 mt-3 justify-content-end">
          <button class="btn btn-light" type="button" :disabled="submitting" @click="closeConfirmSubmit">返回修改</button>
          <button class="btn btn-primary" type="button" :disabled="confirmSubmitDisabled" @click="submitApply">
            {{ submitting ? '提交中...' : confirmCooldown > 0 ? `请等待 ${confirmCooldown}s` : '确认提交报名' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { provinceCityMap, provinceOptions } from '../utils/chinaRegions'

export default {
  name: 'CityExamSignupPublicView',
  data() {
    return {
      loading: true,
      errorText: '',
      detail: {},
      bannerCacheBust: Date.now(),
      bannerRenderKey: '',
      showApplyForm: false,
      submitting: false,
      submitMsg: '',
      lastExamAccount: null,
      showSignupSuccessModal: false,
      agreeNotice: false,
      confirmAccurate: false,
      previewSnapshot: null,
      showPreExamInfo: false,
      showConfirmSubmit: false,
      confirmCooldown: 0,
      confirmTimer: null,
      projectOptions: [],
      defaultBanners: [
        'https://images.unsplash.com/photo-1518609878373-06d740f60d8b?auto=format&fit=crop&w=1920&h=420&q=80',
        'https://images.unsplash.com/photo-1523580846011-d3a5bc25702b?auto=format&fit=crop&w=1920&h=420&q=80',
        'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?auto=format&fit=crop&w=1920&h=420&q=80'
      ],
      form: {
        candidateName: '',
        gender: '',
        idCard: '',
        phone: '',
        province: '',
        city: '',
        projectName: '',
        schoolName: '',
        gradeName: ''
      }
    }
  },
  computed: {
    submitDisabled() {
      if (this.submitting) return true
      if (!this.detail || !this.detail.signupToken) return true
      if (!this.form.candidateName || !this.form.phone || !this.form.idCard) return true
      if (!this.form.gender || !this.form.schoolName || !this.form.gradeName) return true
      if (!this.form.province || !this.form.city) return true
      if (!this.form.projectName) return true
      if (!this.agreeNotice || !this.confirmAccurate) return true
      return false
    },
    confirmSubmitDisabled() {
      if (this.submitting) return true
      if (!this.previewSnapshot) return true
      if (this.confirmCooldown > 0) return true
      return false
    },
    provinceOptions() {
      return provinceOptions
    },
    cityOptions() {
      const p = String(this.form.province || '').trim()
      const list = p && provinceCityMap[p] ? provinceCityMap[p] : []
      return Array.isArray(list) ? list : []
    },
    preExamInfoText() {
      const fallback = this.defaultPreExamInfo()
      const raw = this.detail && this.detail.extraConfig != null ? String(this.detail.extraConfig) : ''
      if (!raw || !raw.trim()) return fallback
      try {
        const parsed = JSON.parse(raw)
        const text = parsed && (parsed.preExamInfo || parsed.preExamNotice || parsed.preExamText)
        const t = text != null ? String(text).trim() : ''
        return t || fallback
      } catch (e) {
        return raw.trim() || fallback
      }
    },
    eventIntroText() {
      const raw = this.detail && this.detail.extraConfig != null ? String(this.detail.extraConfig) : ''
      if (!raw || !raw.trim()) return ''
      try {
        const parsed = JSON.parse(raw)
        const text = parsed && (parsed.eventIntro || parsed.eventIntroduction)
        return text == null ? '' : String(text).trim()
      } catch (e) {
        return ''
      }
    },
    signupSuccessExamTimeText() {
      const t = this.detail && this.detail.examTime
      if (t == null || String(t).trim() === '') return ''
      try {
        return this.$fmtDateTime(t)
      } catch (e) {
        return String(t)
      }
    },
    bannerUrl() {
      const withBust = (url) => {
        const u = String(url || '')
        const sep = u.includes('?') ? '&' : '?'
        return `${u}${sep}v=${encodeURIComponent(String(this.bannerCacheBust || Date.now()))}`
      }
      const custom = String((this.detail && this.detail.bannerUrl) || '').trim()
      if (custom) {
        if (custom.startsWith('data:image')) return custom
        if (custom.startsWith('http://') || custom.startsWith('https://')) return withBust(custom)
        if (custom.startsWith('/')) return withBust(custom)
        return withBust(`/${custom}`)
      }
      const idx = this.detail && this.detail.id ? Number(this.detail.id) % this.defaultBanners.length : 0
      return this.defaultBanners[idx] || this.defaultBanners[0]
    },
    /** 管理员关闭通道，或未到报名开始时间，或已过报名截止时间 */
    isChannelOff() {
      if (!this.detail || this.detail.status == null || this.detail.status === '') return false
      return Number(this.detail.status) === 0
    },
    signupBeforeStart() {
      const d = this.detail || {}
      if (typeof d.signupBeforeStart === 'boolean') return d.signupBeforeStart
      const start = d.signupStartTime
      if (start == null || String(start).trim() === '') return false
      const t = Date.parse(String(start).trim().replace(' ', 'T'))
      return Number.isFinite(t) && Date.now() < t
    },
    signupAfterEnd() {
      const d = this.detail || {}
      if (typeof d.signupAfterEnd === 'boolean') return d.signupAfterEnd
      const end = d.signupEndTime
      if (end == null || String(end).trim() === '') return false
      const t = Date.parse(String(end).trim().replace(' ', 'T'))
      return Number.isFinite(t) && Date.now() > t
    },
    signupWindowActive() {
      return !this.isChannelOff && !this.signupBeforeStart && !this.signupAfterEnd
    },
    signupStatusHeadline() {
      if (this.isChannelOff) return '该报名通道已关闭'
      if (this.signupBeforeStart) return '报名尚未开始'
      if (this.signupAfterEnd) return '赛事报名已结束'
      return '报名开启'
    },
    signupButtonLabel() {
      if (this.isChannelOff) return '通道已关闭'
      if (this.signupBeforeStart) return '报名尚未开始'
      if (this.signupAfterEnd) return '赛事报名已结束'
      return '赛事报名'
    },
    isSignupClosed() {
      return this.isChannelOff || this.signupBeforeStart || this.signupAfterEnd
    }
  },
  watch: {
    'form.province': function () {
      if (this.form.city) this.form.city = ''
    },
    'detail.bannerUrl': function () {
      this.bannerCacheBust = Date.now()
    }
  },
  methods: {
    loadProjectOptions() {
      if (!this.detail || !this.detail.signupToken) return Promise.resolve()
      return fetch(`/api/city-signup/open/${encodeURIComponent(this.detail.signupToken)}/projects`)
        .then((r) => r.json())
        .then((res) => {
          if (!res || res.code !== 200) return
          const list = Array.isArray(res.data) ? res.data : []
          this.projectOptions = list
        })
        .catch(() => {})
    },
    openApplyModal() {
      if (this.isSignupClosed) return
      this.showApplyForm = true
      this.previewSnapshot = null
      this.agreeNotice = false
      this.confirmAccurate = false
      this.closeConfirmSubmit()
      this.loadProjectOptions()
      try {
        document.body.classList.add('city-signup-modal-open')
      } catch (e) {
        void e
      }
    },
    closeApplyModal(force) {
      if (!force && this.submitting) return
      this.showApplyForm = false
      this.closeConfirmSubmit()
      try {
        document.body.classList.remove('city-signup-modal-open')
      } catch (e) {
        void e
      }
    },
    startConfirmCooldown(seconds) {
      const s = Number(seconds || 0)
      this.stopConfirmCooldown()
      this.confirmCooldown = Number.isFinite(s) && s > 0 ? Math.floor(s) : 0
      if (this.confirmCooldown <= 0) return
      this.confirmTimer = setInterval(() => {
        if (this.confirmCooldown <= 1) {
          this.confirmCooldown = 0
          this.stopConfirmCooldown()
          return
        }
        this.confirmCooldown -= 1
      }, 1000)
    },
    stopConfirmCooldown() {
      if (this.confirmTimer) {
        clearInterval(this.confirmTimer)
        this.confirmTimer = null
      }
    },
    openConfirmSubmit() {
      if (!this.detail || !this.detail.signupToken) return
      if (this.submitDisabled) {
        if (!this.form.candidateName || !this.form.phone || !this.form.idCard) {
          window.alert('请先完整填写：姓名、联系电话、身份证号（带 * 为必填）')
          return
        }
        if (!this.form.gender || !this.form.schoolName || !this.form.gradeName) {
          window.alert('请先完整填写：性别、学校、年级（带 * 为必填）')
          return
        }
        if (!this.form.province || !this.form.city) {
          window.alert('请先完整填写：省份、地市（带 * 为必填）')
          return
        }
        if (!this.form.projectName) {
          window.alert('请选择报名项目（带 * 为必填）')
          return
        }
        if (!this.agreeNotice) {
          window.alert('请先勾选：我已同意“考前信息”')
          return
        }
        if (!this.confirmAccurate) {
          window.alert('请先勾选：我已确认上述的信息无误')
          return
        }
        return
      }
      this.previewSnapshot = { ...this.form }
      this.showConfirmSubmit = true
      this.startConfirmCooldown(10)
    },
    closeConfirmSubmit() {
      this.showConfirmSubmit = false
      this.stopConfirmCooldown()
      this.confirmCooldown = 0
    },
    resetForm() {
      this.form = {
        candidateName: '',
        gender: '',
        idCard: '',
        phone: '',
        province: '',
        city: '',
        projectName: '',
        schoolName: '',
        gradeName: ''
      }
      this.previewSnapshot = null
    },
    defaultPreExamInfo() {
      return [
        '【考前信息（模板，可在地市报名编辑中修改）】',
        '',
        '1. 证件与材料',
        '- 请携带本人有效身份证件；如有准考证/通知单，请一并携带。',
        '',
        '2. 入场时间',
        '- 建议提前 30 分钟到达考点，按工作人员引导有序排队入场。',
        '',
        '3. 设备与物品要求',
        '- 严禁携带与考试无关的电子设备进入考场（以现场要求为准）。',
        '- 请自备必要文具（如签字笔等）。',
        '',
        '4. 纪律与违规处理',
        '- 请遵守考场纪律，服从监考人员管理。',
        '- 发现违规将按规定处理。',
        '',
        '5. 信息核对',
        '- 报名信息需真实准确；提交前请再次核对姓名、身份证号、联系电话等关键字段。',
        '',
        '6. 其他',
        '- 如遇特殊情况，请及时联系项目负责人或考点工作人员。'
      ].join('\n')
    },
    openPreExamInfo() {
      this.showPreExamInfo = true
    },
    closePreExamInfo() {
      this.showPreExamInfo = false
    },
    openSignupSuccessModal() {
      this.showSignupSuccessModal = true
      try {
        document.body.classList.add('city-signup-modal-open')
      } catch (e) {
        void e
      }
    },
    closeSignupSuccessModal() {
      this.showSignupSuccessModal = false
      const anyOther =
        this.showApplyForm || this.showConfirmSubmit || this.showPreExamInfo
      if (!anyOther) {
        try {
          document.body.classList.remove('city-signup-modal-open')
        } catch (e) {
          void e
        }
      }
    },
    downloadExamAccountExcel() {
      const a = this.lastExamAccount
      if (!a) return
      const headers = ['姓名', '场次名称', '参赛事项', '参赛时间', '登录账号', '登录密码']
      const row = [
        a.candidateName || '',
        a.examName || '',
        a.projectName || '',
        a.examTime || '',
        a.username || '',
        a.plainPassword || ''
      ]
      const escXml = (v) => String(v == null ? '' : v)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;')
      const xml = [
        '<?xml version="1.0" encoding="UTF-8"?>',
        '<?mso-application progid="Excel.Sheet"?>',
        '<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"',
        ' xmlns:o="urn:schemas-microsoft-com:office:office"',
        ' xmlns:x="urn:schemas-microsoft-com:office:excel"',
        ' xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">',
        ' <Worksheet ss:Name="账号信息">',
        '  <Table>',
        `   <Row>${headers.map((h) => `<Cell><Data ss:Type="String">${escXml(h)}</Data></Cell>`).join('')}</Row>`,
        `   <Row>${row.map((v) => `<Cell><Data ss:Type="String">${escXml(v)}</Data></Cell>`).join('')}</Row>`,
        '  </Table>',
        ' </Worksheet>',
        '</Workbook>'
      ].join('')
      const blob = new Blob(['\uFEFF' + xml], { type: 'application/vnd.ms-excel;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.href = url
      link.download = `考试账号_${a.username || 'export'}.xls`
      link.click()
      URL.revokeObjectURL(url)
    },
    submitApply() {
      if (!this.detail || !this.detail.signupToken) return
      if (!this.previewSnapshot || !this.showConfirmSubmit) {
        this.openConfirmSubmit()
        return
      }
      if (this.confirmCooldown > 0) return
      this.submitting = true
      this.submitMsg = ''
      const payload = this.previewSnapshot && typeof this.previewSnapshot === 'object'
        ? { ...this.previewSnapshot }
        : { ...this.form }
      fetch(`/api/city-signup/open/${encodeURIComponent(this.detail.signupToken)}/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
        .then((r) => r.json())
        .then((res) => {
          if (!res || res.code !== 200) {
            window.alert((res && res.message) || '提交失败，请稍后重试')
            return
          }
          const nameFromPreview = this.previewSnapshot && this.previewSnapshot.candidateName
          const data = res.data || {}
          const ex = data.examAccount
          if (ex && ex.username && ex.plainPassword) {
            this.lastExamAccount = {
              username: String(ex.username),
              plainPassword: String(ex.plainPassword),
              candidateName: String(data.candidateName || nameFromPreview || ''),
              examName: String((this.detail && this.detail.examName) || ''),
              projectName: String(data.projectName || (this.previewSnapshot && this.previewSnapshot.projectName) || ''),
              examTime: this.signupSuccessExamTimeText ? String(this.signupSuccessExamTimeText) : ''
            }
            this.submitMsg = `报名成功！已生成考试账号「${this.lastExamAccount.username}」，请下载保存账号文件（含密码）。`
          } else {
            this.lastExamAccount = null
            this.submitMsg = '报名提交成功，请等待审核/通知。'
          }
          this.resetForm()
          this.closeConfirmSubmit()
          this.closeApplyModal(true)
          this.openSignupSuccessModal()
          this.$nextTick(() => {
            try {
              window.scrollTo({ top: 0, behavior: 'smooth' })
            } catch (e) {
              try {
                window.scrollTo(0, 0)
              } catch (e2) {
                void e2
              }
              void e
            }
          })
        })
        .catch(() => window.alert('提交失败，请检查网络后重试'))
        .finally(() => {
          this.submitting = false
        })
    }
  },
  created() {
    const hash = String(window.location.hash || '')
    const token = hash.startsWith('#/city-signup/')
      ? hash.slice('#/city-signup/'.length).trim()
      : ''
    if (!token) {
      this.loading = false
      this.errorText = '报名地址无效'
      return
    }
    // Reset view state early to avoid showing a previously loaded banner while we fetch new data.
    this.detail = {}
    this.errorText = ''
    this.loading = true
    this.bannerCacheBust = Date.now()
    this.bannerRenderKey = `${token}::${this.bannerCacheBust}`
    fetch(`/api/city-signup/open/${encodeURIComponent(token)}`)
      .then((r) => r.json())
      .then((res) => {
        if (!res || res.code !== 200 || !res.data) {
          this.errorText = (res && res.message) || '报名场次不存在'
          return
        }
        this.detail = res.data
        this.bannerCacheBust = Date.now()
        this.bannerRenderKey = `${token}::${this.bannerCacheBust}`
        this.loadProjectOptions()
      })
      .catch(() => {
        this.errorText = '加载失败，请稍后重试'
      })
      .finally(() => {
        this.loading = false
      })
  }
}
</script>

<style scoped>
.city-signup-page {
  /* 底部留白主要由 .city-signup-fixed-bar-spacer 承担，避免与 Bootstrap 纵向 padding 叠加失效 */
  padding-bottom: 0;
  background: linear-gradient(160deg, #eef6ff 0%, #e4f0ff 55%, #f2f8ff 100%);
}

/* 与 .city-signup-bottom-bar 实际占位大致对齐：内边距 + 按钮高度 + 光效外扩 */
.city-signup-fixed-bar-spacer {
  width: 100%;
  flex-shrink: 0;
  pointer-events: none;
  height: calc(120px + env(safe-area-inset-bottom, 0px));
  min-height: 120px;
}

.city-signup-page--has-intro .city-signup-fixed-bar-spacer {
  /* 有赛事介绍时加长占位，兼容大屏与安全区 */
  height: calc(280px + max(8vh, 48px) + env(safe-area-inset-bottom, 0px));
  min-height: 280px;
}

.city-signup-banner-wrap {
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid #d8e6ff;
  box-shadow: 0 10px 24px rgba(48, 96, 192, 0.14);
  background: #f3f8ff;
  width: 100%;
}

.city-signup-banner-img {
  width: 100%;
  height: auto;
  display: block;
}

.city-signup-banner-skeleton {
  width: 100%;
  aspect-ratio: 16 / 3;
  background: linear-gradient(90deg, #eef6ff 0%, #f7fbff 40%, #eef6ff 80%);
  background-size: 200% 100%;
  animation: citySignupSkeleton 1.2s ease-in-out infinite;
}

@keyframes citySignupSkeleton {
  0% { background-position: 0% 0%; }
  100% { background-position: 200% 0%; }
}

.city-signup-card {
  padding: 18px 18px 22px !important;
  border-radius: 16px !important;
  border-color: #cfe0ff !important;
  background: linear-gradient(165deg, #f1f7ff 0%, #e8f2ff 55%, #f7fbff 100%) !important;
  box-shadow: 0 14px 34px rgba(34, 85, 185, 0.12) !important;
}

.city-signup-head {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.city-signup-title {
  font-weight: 800;
  letter-spacing: 0.2px;
  color: #173a7a;
}

.city-signup-subtitle {
  font-size: 12px;
  letter-spacing: 0.08em;
}

.city-signup-info-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.city-signup-info-card {
  border: 1px solid #d8e6ff;
  border-radius: 14px;
  padding: 12px 14px;
  background: linear-gradient(145deg, #f2f8ff 0%, #e8f2ff 45%, #f7fbff 100%);
  box-shadow: 0 8px 18px rgba(35, 88, 186, 0.08);
  min-width: 0;
}

.city-signup-info-card .k {
  font-size: 14px;
  color: #5c7098;
  margin-bottom: 4px;
  letter-spacing: 0.08em;
}

.city-signup-info-card .v {
  font-size: 16px;
  font-weight: 700;
  color: #1f2a44;
  line-height: 1.5;
  word-break: break-word;
}

.city-signup-intro-card {
  grid-column: 1 / -1;
  scroll-margin-bottom: 32px;
}

.city-signup-intro-text {
  white-space: pre-wrap;
  /* 长文在卡片内滚动，最后一行不会落在固定底栏下方 */
  max-height: max(168px, min(70vh, calc(100vh - 200px)));
  max-height: max(168px, min(70vh, calc(100dvh - 200px)));
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  padding-right: 4px;
  margin-right: -4px;
}

.city-signup-attach-link {
  color: #1d4ed8;
  text-decoration: none;
}

.city-signup-attach-link:hover {
  text-decoration: underline;
}

.city-signup-attach-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.city-signup-bottom-bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 30;
  padding: 12px 12px max(12px, env(safe-area-inset-bottom));
  background: linear-gradient(180deg, rgba(250, 252, 255, 0) 0%, rgba(245, 249, 255, 0.92) 24%, rgba(245, 249, 255, 0.98) 100%);
  backdrop-filter: blur(10px);
  border-top: 1px solid rgba(207, 224, 255, 0.8);
}

.city-signup-bottom-inner {
  max-width: 980px;
  margin: 0 auto;
  display: flex;
  justify-content: center;
}

.city-signup-apply-btn-smoke {
  position: relative;
  min-width: 180px;
  padding: 12px 22px;
  font-weight: 800;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.65);
  color: #fff;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 55%, #b91c1c 100%);
  box-shadow: 0 14px 34px rgba(220, 38, 38, 0.28);
  letter-spacing: 0.14em;
  overflow: visible;
  transform: translateZ(0);
}

.city-signup-apply-btn-smoke:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.city-signup-apply-btn-smoke--disabled {
  background: linear-gradient(135deg, #9aa5b1 0%, #7f8b99 55%, #6b7280 100%) !important;
  border-color: rgba(255, 255, 255, 0.45) !important;
  box-shadow: 0 10px 24px rgba(71, 85, 105, 0.28) !important;
}

.city-signup-apply-btn-smoke .label {
  position: relative;
  z-index: 2;
}

.city-signup-apply-btn-smoke::before {
  content: '';
  position: absolute;
  inset: -10px;
  border-radius: 18px;
  background: radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.25), rgba(255, 255, 255, 0) 60%);
  filter: blur(10px);
  opacity: 0.9;
  z-index: 1;
  animation: cityGlow 2.4s ease-in-out infinite;
  pointer-events: none;
}

.city-signup-apply-btn-smoke:hover {
  filter: brightness(1.03);
  transform: translateY(-1px);
}

.city-signup-apply-btn-smoke .smoke {
  position: absolute;
  width: 46px;
  height: 30px;
  border-radius: 999px;
  background: radial-gradient(circle at 30% 40%, rgba(255, 255, 255, 0.72), rgba(255, 255, 255, 0) 70%);
  filter: blur(6px);
  opacity: 0.85;
  z-index: 0;
  pointer-events: none;
  mix-blend-mode: screen;
  animation: citySmoke 2.6s ease-in-out infinite;
}

.city-signup-apply-btn-smoke .smoke.s1 {
  left: -18px;
  top: 8px;
  animation-delay: 0s;
}
.city-signup-apply-btn-smoke .smoke.s2 {
  right: -22px;
  top: 10px;
  width: 54px;
  height: 34px;
  animation-delay: 0.4s;
}
.city-signup-apply-btn-smoke .smoke.s3 {
  left: 18px;
  bottom: -14px;
  width: 60px;
  height: 38px;
  animation-delay: 0.8s;
}
.city-signup-apply-btn-smoke .smoke.s4 {
  right: 22px;
  bottom: -12px;
  width: 52px;
  height: 34px;
  animation-delay: 1.1s;
}
.city-signup-apply-btn-smoke .smoke.s5 {
  left: 50%;
  top: -18px;
  transform: translateX(-50%);
  width: 68px;
  height: 40px;
  animation-delay: 1.5s;
}

@keyframes citySmoke {
  0% {
    transform: translate(0, 0) scale(0.95);
    opacity: 0.55;
  }
  35% {
    transform: translate(8px, -6px) scale(1.05);
    opacity: 0.85;
  }
  70% {
    transform: translate(-6px, -10px) scale(1.12);
    opacity: 0.72;
  }
  100% {
    transform: translate(0, 0) scale(0.98);
    opacity: 0.55;
  }
}

@keyframes cityGlow {
  0% {
    opacity: 0.65;
    transform: scale(0.98);
  }
  50% {
    opacity: 0.95;
    transform: scale(1.03);
  }
  100% {
    opacity: 0.65;
    transform: scale(0.98);
  }
}

.city-signup-modal {
  position: fixed;
  inset: 0;
  z-index: 60;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.city-signup-success-layer {
  z-index: 100;
}

.city-signup-success-card {
  width: min(520px, 100%);
}

.city-signup-success-body {
  font-size: 15px;
  line-height: 1.65;
  color: #334155;
}

.city-signup-modal-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(17, 24, 39, 0.42);
  backdrop-filter: blur(6px);
}

.city-signup-modal-card {
  position: relative;
  z-index: 1;
  width: min(860px, 100%);
  border-radius: 16px;
  background: linear-gradient(165deg, #f7fbff 0%, #eef6ff 55%, #ffffff 100%);
  border: 1px solid rgba(207, 224, 255, 0.95);
  box-shadow: 0 24px 70px rgba(16, 24, 40, 0.24);
  padding: 14px 14px 16px;
  max-height: min(76vh, 720px);
  overflow: auto;
}

.city-signup-modal-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 4px 2px 10px;
}

.city-signup-modal-title {
  font-weight: 900;
  color: #173a7a;
  letter-spacing: 0.04em;
}

.city-signup-modal-subtitle {
  font-size: 12px;
  margin-top: 2px;
}

.city-signup-modal-close {
  width: 36px;
  height: 36px;
  border-radius: 12px;
  border: 1px solid rgba(207, 224, 255, 0.9);
  background: rgba(255, 255, 255, 0.8);
  color: #334155;
  font-size: 22px;
  line-height: 1;
}

.city-signup-modal-close:hover {
  filter: brightness(1.02);
}

.city-signup-field-label {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  margin: 0 0 6px;
  font-size: 13px;
  font-weight: 800;
  color: #173a7a;
  letter-spacing: 0.06em;
}

.city-signup-field-hint {
  font-weight: 600;
  letter-spacing: 0;
  color: #64748b;
}

.req-star {
  color: #ef4444;
  font-weight: 900;
}

.city-signup-confirm-box {
  border: 1px dashed rgba(191, 214, 255, 0.95);
  background: rgba(255, 255, 255, 0.6);
  border-radius: 14px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.city-signup-check {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: #334155;
  user-select: none;
}

.city-signup-check input {
  width: 16px;
  height: 16px;
}

.city-signup-link {
  color: #1d4ed8;
  text-decoration: underline;
  font-weight: 800;
}

.city-signup-link:hover {
  filter: brightness(1.03);
}

.city-signup-preinfo {
  margin: 0;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid rgba(207, 224, 255, 0.95);
  background: rgba(255, 255, 255, 0.7);
  color: #334155;
  font-size: 13px;
  line-height: 1.65;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Arial, "PingFang SC", "Microsoft YaHei", sans-serif;
}

.city-signup-preview {
  border: 1px solid rgba(207, 224, 255, 0.95);
  border-radius: 14px;
  background: linear-gradient(165deg, rgba(255, 255, 255, 0.75), rgba(245, 250, 255, 0.92));
  padding: 10px 12px;
}

.city-signup-preview-title {
  font-weight: 900;
  color: #173a7a;
  letter-spacing: 0.06em;
  font-size: 13px;
  margin-bottom: 8px;
}

.city-signup-preview-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.city-signup-preview-grid .item {
  border: 1px solid rgba(216, 230, 255, 0.9);
  border-radius: 12px;
  padding: 8px 10px;
  background: rgba(255, 255, 255, 0.65);
  min-width: 0;
}

.city-signup-preview-grid .item.wide {
  grid-column: 1 / -1;
}

.city-signup-preview-grid .k {
  font-size: 12px;
  color: #5c7098;
  letter-spacing: 0.08em;
  margin-bottom: 2px;
}

.city-signup-preview-grid .v {
  font-size: 14px;
  color: #1f2a44;
  font-weight: 800;
  word-break: break-word;
}

/* 信息核验弹窗：整体字号放大，便于查看 */
.city-signup-modal-card--confirm .city-signup-modal-title {
  font-size: 18px;
}

.city-signup-modal-card--confirm .city-signup-modal-subtitle {
  font-size: 14px;
  line-height: 1.6;
}

.city-signup-modal-card--confirm .city-signup-preview-title {
  font-size: 15px;
}

.city-signup-modal-card--confirm .city-signup-preview-grid .k {
  font-size: 13px;
}

.city-signup-modal-card--confirm .city-signup-preview-grid .v {
  font-size: 17px;
  line-height: 1.55;
}

@media (max-width: 768px) {
  .city-signup-card {
    padding: 14px 14px 18px !important;
  }

  .city-signup-info-grid {
    grid-template-columns: 1fr;
  }

  .city-signup-preview-grid {
    grid-template-columns: 1fr;
  }
}
</style>
